import React from 'react';
import { Sparkles, FileText, Code, Image, Video, CheckCircle } from 'lucide-react';
import { useSEO } from '../../hooks/useSEO';
import { Breadcrumb } from '../../components/Breadcrumb';
import { useBanner } from '../../contexts/BannerContext';

export const CognitiveGeneratePage: React.FC = () => {
  useSEO('cognitive-generate' as any);
  const { isBannerVisible, bannerHeight } = useBanner();

  return (
    <>
      <Breadcrumb />
      <main className="pb-20">
      <section 
        className="px-4 sm:px-6 lg:px-8 mb-20"
        style={{ 
          paddingTop: isBannerVisible ? `${bannerHeight + 64 + 44 + 16}px` : '124px' // 64px header + 44px breadcrumb + 16px extra space
        }}
      >
        <div className="max-w-7xl mx-auto text-center">
          <div className="w-20 h-20 bg-gradient-to-r from-blue-600 to-blue-700 rounded-3xl mx-auto mb-8 flex items-center justify-center">
            <Sparkles className="w-10 h-10 text-white" />
          </div>
          <h1 
            className="text-5xl md:text-6xl font-bold mb-8 py-4 px-2 leading-loose bg-gradient-to-r from-blue-600 to-blue-700 bg-clip-text text-transparent overflow-visible min-h-fit"
            style={{ lineHeight: '1.8', minHeight: 'auto', overflow: 'visible', paddingTop: '16px', paddingBottom: '16px' }}
          >
            Cognitive Generate
          </h1>
          <p className="text-xl text-gray-700 dark:text-gray-300 max-w-4xl mx-auto leading-relaxed">
            Harness the power of generative AI to create content, code, and solutions at scale. 
            Transform creativity and productivity with cutting-edge AI generation capabilities.
          </p>
        </div>
      </section>

      <div className="px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <section className="grid lg:grid-cols-2 gap-12 mb-16">
            <div className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-3xl border border-gray-200 dark:border-white/10 p-8">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">Generative AI Solutions</h2>
              <div className="space-y-6">
                <div className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mr-3" />
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Custom GPT Implementations</h3>
                    <p className="text-gray-700 dark:text-gray-300">Deploy tailored ChatGPT and GPT-4 solutions for your specific business needs.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mr-3" />
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Content Generation Platforms</h3>
                    <p className="text-gray-700 dark:text-gray-300">Automated content creation for marketing, documentation, and communication.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mr-3" />
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Multimodal AI Solutions</h3>
                    <p className="text-gray-700 dark:text-gray-300">Generate and process text, images, video, and audio with unified AI systems.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mr-3" />
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">AI-Powered Code Generation</h3>
                    <p className="text-gray-700 dark:text-gray-300">Accelerate development with intelligent code generation and documentation.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-3xl border border-gray-200 dark:border-white/10 p-8">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">Generation Capabilities</h2>
              <div className="grid grid-cols-2 gap-6">
                <div className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl mx-auto mb-4 flex items-center justify-center">
                    <FileText className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Text & Content</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-300">Articles, blogs, marketing copy, documentation</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl mx-auto mb-4 flex items-center justify-center">
                    <Code className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Code & Scripts</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-300">APIs, applications, automation scripts</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl mx-auto mb-4 flex items-center justify-center">
                    <Image className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Images & Graphics</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-300">Marketing visuals, product images, designs</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl mx-auto mb-4 flex items-center justify-center">
                    <Video className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Video & Audio</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-300">Training videos, presentations, voiceovers</p>
                </div>
              </div>
            </div>
          </section>

          <section className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-3xl border border-gray-200 dark:border-white/10 p-8 mb-16">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-8 text-center">Industry Applications</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <h3 className="text-xl font-bold text-blue-600 dark:text-blue-400 mb-4">Marketing & Sales</h3>
                <p className="text-gray-700 dark:text-gray-300">
                  Generate personalized marketing campaigns, sales copy, product descriptions, and social media content at scale.
                </p>
              </div>
              <div className="text-center">
                <h3 className="text-xl font-bold text-blue-600 dark:text-blue-400 mb-4">Software Development</h3>
                <p className="text-gray-700 dark:text-gray-300">
                  Accelerate coding with AI-generated functions, documentation, test cases, and automated code reviews.
                </p>
              </div>
              <div className="text-center">
                <h3 className="text-xl font-bold text-blue-600 dark:text-blue-400 mb-4">Education & Training</h3>
                <p className="text-gray-700 dark:text-gray-300">
                  Create educational content, training materials, assessments, and personalized learning experiences.
                </p>
              </div>
            </div>
          </section>

          <section className="bg-gradient-to-r from-blue-600/10 to-blue-700/10 rounded-3xl border border-blue-200 dark:border-blue-500/30 p-8 mb-16">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">Key Benefits</h2>
              <p className="text-gray-700 dark:text-gray-300 text-lg">
                Generative AI solutions designed to transform how businesses create and develop content
              </p>
            </div>
            <div className="grid md:grid-cols-4 gap-6 text-center">
              <div>
                <div className="text-4xl font-bold text-blue-600 dark:text-blue-400 mb-2">Enhanced</div>
                <div className="text-sm text-gray-600 dark:text-gray-300">Productivity</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-blue-600 dark:text-blue-400 mb-2">Accelerated</div>
                <div className="text-sm text-gray-600 dark:text-gray-300">Development</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-blue-600 dark:text-blue-400 mb-2">Optimized</div>
                <div className="text-sm text-gray-600 dark:text-gray-300">Costs</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-blue-600 dark:text-blue-400 mb-2">Scaled</div>
                <div className="text-sm text-gray-600 dark:text-gray-300">Content Creation</div>
              </div>
            </div>
          </section>

          <section className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-3xl border border-gray-200 dark:border-white/10 p-8 text-center">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">
              Ready to Generate at Scale?
            </h2>
            <p className="text-gray-700 dark:text-gray-300 text-lg mb-8 max-w-3xl mx-auto">
              Join the generative AI revolution. Transform your content creation, development workflows, 
              and business processes with cutting-edge AI generation capabilities.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => window.location.href = '/contact'}
                className="px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold rounded-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
              >
                Start Generating Today
              </button>
              <button
                onClick={() =>
                  window.open(
                    "https://outlook.office.com/book/CognitiveCraftHub2@cognitivecrafthub.com/?ismsaljsauthenabled",
                    "_blank"
                  )
                }
                className="px-8 py-4 border-2 border-gray-300 dark:border-white/30 text-gray-700 dark:text-white font-semibold rounded-lg hover:bg-gray-100 dark:hover:bg-white/10 hover:border-gray-400 dark:hover:border-white/50 transition-all duration-300"
              >
                Explore Solutions
              </button>
            </div>
          </section>
        </div>
      </div>
    </main>
    </>
  );
};